import { type KeyboardEvent, type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "../api";
import type { TaskRecord } from "../types";
import { insertMention, mentionAt, mentionChoices } from "./taskMention";
import { statusLabel } from "./taskLabels";

/**
 * `@` in the composer: a picker of the sender's todo and in-progress tasks, searched by title, that
 * writes the chosen task's literal `@<id>` at the caret. Nothing else about the message changes; the
 * agent reads each id with get_task.
 */
export function useTaskMentions({
	enabled,
	draft,
	setDraft,
	textarea,
}: {
	/** The session's agent can read tasks, and the person can type. */
	enabled: boolean;
	draft: string;
	setDraft: (text: string) => void;
	textarea: RefObject<HTMLTextAreaElement | null>;
}): {
	/** For the textarea's change, select and focus: where its caret is now. */
	onCaret: (el: HTMLTextAreaElement) => void;
	onBlur: () => void;
	/** True when the picker took the key, so the composer leaves it alone. */
	onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => boolean;
	picker: ReactNode;
} {
	// Null while the textarea is unfocused or has a range selected: no picker then.
	const [caret, setCaret] = useState<number | null>(null);
	const [tasks, setTasks] = useState<TaskRecord[] | null>(null);
	const [failed, setFailed] = useState(false);
	// The `@` the picker was closed on with Escape; it stays closed there until another `@` is typed.
	const [dismissed, setDismissed] = useState<number | null>(null);
	const [highlight, setHighlight] = useState(0);
	const list = useRef<HTMLDivElement>(null);
	const nextCaret = useRef<number | null>(null);

	const mention = enabled && caret !== null ? mentionAt(draft, caret) : null;
	const open = mention !== null && mention.start !== dismissed;
	const choices = open && tasks ? mentionChoices(tasks, mention.query) : [];
	// Nothing shows while the list loads, nor once nothing matches what was typed after the `@`.
	const shown = open && (failed || (tasks !== null && (choices.length > 0 || mention.query === "")));

	// Fetched afresh whenever an `@` opens the picker, so a task added meanwhile is offered too.
	useEffect(() => {
		if (!open) return;
		let live = true;
		setTasks(null);
		setFailed(false);
		api
			.tasks()
			.then((r) => {
				if (live) setTasks(r.tasks);
			})
			.catch(() => {
				if (live) setFailed(true);
			});
		return () => {
			live = false;
		};
	}, [open]);

	const typing = mention !== null;
	useEffect(() => {
		if (!typing) setDismissed(null);
	}, [typing]);
	useEffect(() => setHighlight(0), [mention?.start, mention?.query]);
	const current = Math.min(highlight, choices.length - 1);

	// The caret goes after an inserted mention once the textarea holds the new text.
	useLayoutEffect(() => {
		const at = nextCaret.current;
		const el = textarea.current;
		if (at === null || !el) return;
		nextCaret.current = null;
		el.focus();
		el.setSelectionRange(at, at);
	}, [draft, textarea]);

	function choose(task: TaskRecord) {
		if (!mention) return;
		const next = insertMention(draft, mention, task.id);
		nextCaret.current = next.caret;
		setCaret(next.caret);
		setDraft(next.text);
	}

	/** Moves the highlight by the arrow keys, scrolling only the list, never the page around it. */
	function move(step: number) {
		const next = (current + step + choices.length) % choices.length;
		setHighlight(next);
		const box = list.current;
		const item = box?.querySelectorAll<HTMLElement>('[role="option"]')[next];
		if (!box || !item) return;
		if (item.offsetTop < box.scrollTop) box.scrollTop = item.offsetTop;
		else if (item.offsetTop + item.offsetHeight > box.scrollTop + box.clientHeight) box.scrollTop = item.offsetTop + item.offsetHeight - box.clientHeight;
	}

	function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): boolean {
		if (!shown || e.nativeEvent.isComposing) return false;
		if (e.key === "Escape") {
			e.preventDefault();
			setDismissed(mention.start);
			return true;
		}
		if (choices.length === 0) return false;
		if (e.key === "ArrowDown" || e.key === "ArrowUp") {
			e.preventDefault();
			move(e.key === "ArrowDown" ? 1 : -1);
			return true;
		}
		if ((e.key === "Enter" || e.key === "Tab") && !e.shiftKey) {
			e.preventDefault();
			choose(choices[current]!);
			return true;
		}
		return false;
	}

	const picker = shown ? (
		// Pressing anywhere in it, its scrollbar too, keeps the textarea focused and its caret where the mention is.
		<div className="task-picker" ref={list} role="listbox" aria-label="引用任务" onMouseDown={(e) => e.preventDefault()}>
			<p className="task-picker-head">引用任务</p>
			{failed ? (
				<p className="hint">加载任务失败</p>
			) : choices.length === 0 ? (
				<p className="hint">暂无待办或进行中任务</p>
			) : (
				choices.map((t, i) => (
					<button
						key={t.id}
						type="button"
						role="option"
						aria-selected={i === current}
						className={i === current ? "current" : undefined}
						onMouseEnter={() => setHighlight(i)}
						onClick={() => choose(t)}
					>
						<span className="task-picker-title">{t.title}</span>
						<span className="task-picker-meta">
							{statusLabel(t)}
							{t.projectTitle && ` · ${t.projectTitle}`}
						</span>
					</button>
				))
			)}
		</div>
	) : null;

	return {
		onCaret: (el) => setCaret(el.selectionStart === el.selectionEnd ? el.selectionStart : null),
		onBlur: () => setCaret(null),
		onKeyDown,
		picker,
	};
}
